import { BehaviorSubject, combineLatest, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { Store } from '@ngxs/store';

import { PaymentMethodsStripeService } from '@app/client/settings/shared/modules/payment-methods/services/payment-methods-stripe.service';
import {
  GetPaymentMethods,
  GetPaymentMethodSecret,
  SetPaymentMethodAsDefault,
} from '@app/core/store/states/customer/payment-methods/payment-methods-state.actions';
import { PaymentMethodType } from '@app/core/store/states/customer/payment-methods/payment-methods-state.models';
import { PaymentMethodsState } from '@app/core/store/states/customer/payment-methods/payment-methods.state';
import { DisplayToast } from '@app/core/store/states/ui/ui-state.actions';
import { assertArray } from '@app/core/utilities/array.utilities';
import { lastValue } from '@app/shared/operators/last-value.operator';
import { shareRef } from '@app/shared/operators/share-ref.operator';

@Component({
  standalone: false,
  selector: 'adspt-payment-methods-new',
  templateUrl: './payment-methods-new.component.html',
  styleUrls: ['./payment-methods-new.component.scss'],
  providers: [PaymentMethodsStripeService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentMethodsNewComponent implements OnInit {
  @Input()
  alwaysDefault?: boolean;

  @Input()
  newSubscription?: boolean;

  private readonly fields: Record<PaymentMethodType, string[]> = {
    [PaymentMethodType.SEPA]: ['fullName', 'iban'],
    [PaymentMethodType.Card]: ['cardNumber', 'cardExpiry', 'cardCvc', 'nameOnCard'],
    [PaymentMethodType.Alipay]: ['fullName'],
  };

  private readonly nameFields: Record<PaymentMethodType, string> = {
    [PaymentMethodType.SEPA]: 'fullName',
    [PaymentMethodType.Card]: 'nameOnCard',
    [PaymentMethodType.Alipay]: 'fullName',
  };

  readonly setPaymentMethodProvider$ = new BehaviorSubject<PaymentMethodType>(PaymentMethodType.SEPA);

  readonly allowedPaymentMethods$ = this.store.select(PaymentMethodsState.setup).pipe(
    map((setup) => assertArray(setup?.types)),
    map((types) => (!types.length ? [PaymentMethodType.Card] : types)),
    shareRef(),
  );

  readonly paymentMethodProvider$ = combineLatest([this.allowedPaymentMethods$, this.setPaymentMethodProvider$]).pipe(
    map(([types, selectedType]) => (types.includes(selectedType) ? selectedType : types[0])),
    shareRef(),
  );

  readonly paymentMethodProviders$ = this.allowedPaymentMethods$.pipe(
    map((types) =>
      Object.values(PaymentMethodType)
        .map((type) => ({
          type,
          disabled: !types.includes(type),
          name: {
            [PaymentMethodType.SEPA]: 'sepa-bank',
            [PaymentMethodType.Card]: 'credit-card',
            [PaymentMethodType.Alipay]: 'alipay',
          }[type],
        }))
        .sort((method) => (method.disabled ? 1 : -1)),
    ),
    shareRef(1),
  );

  readonly stripeSca =
    this.ts.currentLang === 'de'
      ? 'https://stripe.com/de/guides/strong-customer-authentication'
      : 'https://stripe.com/en-de/guides/strong-customer-authentication';

  readonly form$: Observable<FormGroup> = this.paymentMethodProvider$.pipe(
    distinctUntilChanged(),
    filter((method) => !!method),
    map((paymentMethod) =>
      this.fb.group(
        this.fields[paymentMethod].reduce(
          (form, field) => ({
            ...form,
            [field]: [null, [Validators.required]],
          }),
          {},
        ),
      ),
    ),
    shareRef(1),
  );

  constructor(
    private fb: FormBuilder,
    private ts: TranslateService,
    private store: Store,
    readonly pms: PaymentMethodsStripeService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new GetPaymentMethodSecret());
  }

  submitForm(): Observable<boolean> {
    const type = lastValue(this.paymentMethodProvider$);

    return forkJoin([this.store.selectOnce(PaymentMethodsState.setup)]).pipe(
      switchMap(([setup]) => {
        const form = lastValue(this.form$);

        return this.pms.submit(
          { name: form.get(this.nameFields[type]).value },
          type,
          setup.setupSecret,
          this.newSubscription,
        );
      }),
      filter((result) => !!result),
      switchMap(({ setupIntent, error }) => {
        if (error || !setupIntent?.payment_method) {
          return throwError(error);
        }

        return this.store.selectOnce(PaymentMethodsState.methods).pipe(
          map((methods) => !methods.some((method) => method.default)),
          switchMap((setAsDefault) =>
            this.store.dispatch(
              setAsDefault
                ? new SetPaymentMethodAsDefault(setupIntent.payment_method.toString())
                : new GetPaymentMethods(),
            ),
          ),
        );
      }),
      map(() => {
        this.store.dispatch([
          new DisplayToast({
            title: this.ts.instant(
              {
                [PaymentMethodType.Card]: 'settings.cc.success',
                [PaymentMethodType.SEPA]: 'settings.sepa.success',
                [PaymentMethodType.Alipay]: 'settings.alipay.success',
              }[type],
            ),
          }),
          new GetPaymentMethods(),
        ]);

        return true;
      }),
      catchError((error) => {
        this.store.dispatch(
          new DisplayToast({
            title:
              (error && error.message) ||
              `${lastValue(this.ts.get('common.error-msg'))} ${lastValue(this.ts.get('common.contact-service'))}`,
            type: 'error',
          }),
        );

        return of(false);
      }),
    );
  }
}
